/**
 * The shared SFX bank at RUNTIME — how a vocabulary cue becomes a sound.
 *
 * `bank/sfx-bank-v1.json` is the reviewed, digest-pinned artifact the
 * generator in `tools/audio-bank/` writes (never hand-edited): one entry per
 * vocabulary cue × style family × variant, each carrying its fully resolved
 * sfxr parameters and the `contentSha256` of the bytes those parameters
 * render to. This module renders an entry in the browser through the SAME
 * `sfxr.ts` the generator used and hands the bytes to {@link AudioEngine.loadSample},
 * which hashes them and REFUSES a mismatch. The pin is the point: a game binds
 * a specific reviewed sound, and a drifted renderer, a corrupted artifact or
 * an engine that disagrees about arithmetic produces silence, never a
 * different sound.
 *
 * This replaced the procedural `DEFAULT_CUES` recipes (Milestone 2 row 2.4):
 * a cue's sound now has exactly one source. Before this, the nine names the
 * recipes covered were audible and the other fifteen vocabulary cues were
 * authored-valid, bank-backed and silent.
 *
 * The cue NAMES are owned by `@djgg/creations` (`cueVocabulary.ts`); the bank
 * artifact is generated against that source and its test fails when they
 * disagree, so reading the cue set out of the artifact here is reading a
 * pinned output of the vocabulary, not a second copy of it.
 */
import artifact from '../bank/sfx-bank-v1.json' with { type: 'json' };
import type { SfxFamily } from '@djgg/core';
import type { AudioEngine } from './engine.js';
import { encodeWav, normalize, renderSamples, resolveParams } from './sfxr.js';
import type { AudioLoadStats } from './types.js';

/** One rendered-and-pinned bank entry, as the artifact records it. */
export interface SfxBankEntry {
  readonly id: string;
  readonly cue: string;
  readonly family: string;
  readonly variant: string;
  readonly contentSha256: string;
  readonly bytes: number;
  readonly durationMs: number;
  readonly peak: number;
  /** Fully resolved sfxr parameters; re-validated by `resolveParams` at render
   *  time so a corrupted artifact is refused rather than rendered. */
  readonly params: Readonly<Record<string, unknown>>;
}

/** The reviewed artifact, typed at the boundary. */
export const SFX_BANK_V1: {
  readonly bankVersion: number;
  readonly cueVocabularyVersion: number;
  readonly families: Readonly<Record<SfxFamily, { readonly label: string; readonly description: string }>>;
  readonly variants: readonly { readonly id: string; readonly label: string }[];
  readonly entries: readonly SfxBankEntry[];
} = artifact;

/**
 * The family every creation binds today. The bank was built with two reviewed
 * families (`arcade`, the sfxr voice ADR 0009 chose it for, and `soft`), but
 * nothing a creation authors selects one yet — a declared family is a manifest
 * field with its own prompt teaching, and that is a later row. Until then this
 * is the ONE place the choice is made.
 */
export const DEFAULT_SFX_FAMILY: SfxFamily = 'arcade';

/** Every cue the bank can play — the vocabulary, as pinned by the artifact. */
export const SFX_BANK_CUES: ReadonlySet<string> = new Set(SFX_BANK_V1.entries.map((entry) => entry.cue));

/**
 * The engine sample id a bank entry loads under. The `bank/` namespace cannot
 * collide with a creator's uploaded `variantId`, which `@djgg/core` restricts
 * to `[A-Za-z0-9_-]` — so a per-match creator binding unloading its own ids can
 * never remove a bank sample, and vice versa.
 */
export function bankSampleId(entryId: string): string {
  return `bank/${entryId}`;
}

/** The entries for one cue in one family, in variant order (v1, v2). */
export function bankEntries(cue: string, family: SfxFamily): SfxBankEntry[] {
  return SFX_BANK_V1.entries
    .filter((entry) => entry.cue === cue && entry.family === family)
    .sort((a, b) => (a.variant < b.variant ? -1 : a.variant > b.variant ? 1 : 0));
}

/** Resolve one immutable entry identity. Used by Studio audition and per-event
 * overrides; a mismatched digest is rejected by the caller, never substituted. */
export function bankEntry(entryId: string): SfxBankEntry | undefined {
  return SFX_BANK_V1.entries.find(entry => entry.id === entryId);
}

/**
 * Render one entry to the canonical WAV bytes its digest pins. Deterministic by
 * construction (see `sfxr.ts`), so these bytes hash to `entry.contentSha256`
 * on every engine — and when they do not, the engine refuses them.
 */
export function renderBankEntry(entry: SfxBankEntry): ArrayBuffer {
  const samples = normalize(renderSamples(resolveParams(entry.params, entry.id), entry.id), entry.peak);
  if (!samples) throw new Error(`${entry.id} renders effectively silent; the artifact is not the reviewed one`);
  return encodeWav(samples).buffer;
}

/** A bound bank: which cues resolved, and how their loads are going. */
export interface SfxBankBinding {
  /** Settles when every requested entry has loaded or been refused. */
  readonly ready: Promise<void>;
  readonly stats: AudioLoadStats;
}

/**
 * Register every requested vocabulary cue on `engine` as a sample cue and load
 * its variants by pinned digest. Names outside the bank are ignored here: a
 * native game's own procedural recipes live in the same registry under their
 * own names, and `engine.play` resolves both through one lookup.
 *
 * Each cue registers immediately (so `play` resolves it) and its bytes load
 * asynchronously (the digest check is async); until a variant has loaded the
 * engine drops the cue rather than queueing it, the same rule as a suspended
 * context. A digest mismatch leaves the cue silent and counted in `stats.failed`.
 */
export function bindSfxBank(
  engine: AudioEngine,
  cues: Iterable<string>,
  family: SfxFamily = DEFAULT_SFX_FAMILY,
): SfxBankBinding {
  const states = new Map<string, 'pending' | 'loaded' | 'failed'>();
  const loads: Promise<void>[] = [];
  const wanted = [...new Set(cues)].filter((cue) => SFX_BANK_CUES.has(cue)).sort();
  for (const cue of wanted) {
    const entries = bankEntries(cue, family);
    if (entries.length === 0) continue;
    engine.registerSampleCue(cue, entries.map((entry) => bankSampleId(entry.id)));
    for (const entry of entries) {
      const id = bankSampleId(entry.id);
      states.set(id, 'pending');
      // Rendered and handed over NOW, so the load is in flight from this call:
      // an engine disposed before the hash completes refuses it (its sample
      // epoch moved), rather than a load that begins later reviving the engine.
      let load: Promise<boolean>;
      try {
        load = engine.loadSample(id, renderBankEntry(entry), entry.contentSha256);
      } catch {
        load = Promise.resolve(false);
      }
      loads.push(load.catch(() => false).then((loaded) => { states.set(id, loaded ? 'loaded' : 'failed'); }));
    }
  }
  return {
    ready: Promise.all(loads).then(() => {}),
    get stats(): AudioLoadStats {
      let loaded = 0; let failed = 0; let pending = 0;
      for (const state of states.values()) { if (state === 'loaded') loaded++; else if (state === 'failed') failed++; else pending++; }
      return { loaded, failed, pending };
    },
  };
}
